import { isPlaceholderThumbnailUrl } from '@/constants/placeholderThumbnails';
import { v1, toResult, type V1Result } from '@/infrastructure/api/v1/client';
import { listAllIn, mapConcurrently } from '@/infrastructure/api/v1/lists';

const VISUAL_ASSET_CATEGORIES = ['prop', 'character', 'mask', 'image', 'video', 'open_brush'];

export interface SceneScreenshotRow {
  scene_instance_id?: string;
  scene_instance_cover_img_url?: string | null;
  updated_at?: string | null;
}

export function isUsableScreenshotUrl(url: string | null | undefined): boolean {
  if (!url || isPlaceholderThumbnailUrl(url)) return false;

  try {
    const { protocol, hostname } = new URL(url);
    if (protocol !== 'http:' && protocol !== 'https:') return false;
    return hostname !== 'localhost' && hostname !== '127.0.0.1' && hostname !== '::1';
  } catch {
    return false;
  }
}

export function pickNewestScreenshotByKey<T extends SceneScreenshotRow>(
  rows: T[],
  keyOf: (row: T) => string | null | undefined,
  eligibleSceneInstanceIds?: Set<string>
): Map<string, string> {
  const newest = new Map<string, { url: string; updatedAt: number }>();

  for (const row of rows) {
    const key = keyOf(row);
    const url = row.scene_instance_cover_img_url;
    if (!key || !isUsableScreenshotUrl(url)) continue;
    if (eligibleSceneInstanceIds && !eligibleSceneInstanceIds.has(row.scene_instance_id ?? '')) continue;

    const updatedAt = row.updated_at ? new Date(row.updated_at).getTime() : 0;
    const current = newest.get(key);
    if (!current || updatedAt > current.updatedAt) {
      newest.set(key, { url: url as string, updatedAt });
    }
  }

  return new Map([...newest].map(([key, value]) => [key, value.url]));
}

/** Per-scene visual-content answers, keyed by user and scene, shared by the screenshot reads of one page load. */
export type VisualContentCache = Map<string, Promise<V1Result<boolean>>>;

export async function fetchSceneInstancesWithVisualContent(
  sceneInstanceIds: string[],
  userId: string,
  cache: VisualContentCache = new Map(),
): Promise<Set<string>> {
  const withContent = new Set<string>();
  if (sceneInstanceIds.length === 0 || !userId) return withContent;

  try {
    // One visual asset answers the question, so each scene asks for at most one, once per cache
    const scenes = [...new Set(sceneInstanceIds)];
    const { data, error } = await mapConcurrently<string, boolean>(scenes, (sceneId) => {
      const key = `${userId}:${sceneId}`;
      let answer = cache.get(key);
      if (!answer) {
        answer = toResult(v1.GET('/api/v1/asset-instances', {
          params: { query: { sceneUniqueId: [sceneId], userUniqueId: userId, category: VISUAL_ASSET_CATEGORIES, limit: 1 } },
        })).then((result): V1Result<boolean> =>
          result.error ? { data: null, error: result.error } : { data: result.data.items.length > 0, error: null });
        cache.set(key, answer);
      }
      return answer;
    });

    if (error) throw error;

    scenes.forEach((sceneId, i) => {
      if (data[i]) withContent.add(sceneId);
    });
  } catch (error) {
    console.error('Error checking scene visual content:', error);
  }

  return withContent;
}

async function fetchScreenshotsByColumn(
  column: 'scene_origin_id' | 'project_instance_id',
  ids: string[],
  userId: string,
  cache?: VisualContentCache,
): Promise<Map<string, string>> {
  if (ids.length === 0 || !userId) return new Map();

  try {
    const { data, error } = await listAllIn(ids, (group, page) =>
      v1.GET('/api/v1/scenes', {
        params: {
          query: {
            ...(column === 'scene_origin_id' ? { storyUniqueId: group } : { projectUniqueId: group }),
            userUniqueId: userId,
            hasCoverImage: true,
            ...page,
          },
        },
      }),
    );

    if (error) throw error;
    const rows: Array<SceneScreenshotRow & Record<string, string | null | undefined>> = data.map((scene) => ({
      [column]: column === 'scene_origin_id' ? scene.storyUniqueId : scene.projectUniqueId,
      scene_instance_id: scene.uniqueId,
      scene_instance_cover_img_url: scene.coverImgUrl,
      updated_at: scene.updatedAt,
    }));

    const usableRows = rows.filter((row) => isUsableScreenshotUrl(row.scene_instance_cover_img_url));
    const withContent = await fetchSceneInstancesWithVisualContent(
      usableRows.map((row) => row.scene_instance_id).filter((id): id is string => !!id),
      userId,
      cache,
    );

    return pickNewestScreenshotByKey(usableRows, (row) => row[column], withContent);
  } catch (error) {
    console.error('Error fetching scene screenshots:', error);
    return new Map();
  }
}

export function fetchScreenshotsBySceneOrigin(
  sceneOriginIds: string[],
  userId: string,
  cache?: VisualContentCache,
): Promise<Map<string, string>> {
  return fetchScreenshotsByColumn('scene_origin_id', sceneOriginIds, userId, cache);
}

export function fetchScreenshotsByProject(
  projectInstanceIds: string[],
  userId: string,
  cache?: VisualContentCache,
): Promise<Map<string, string>> {
  return fetchScreenshotsByColumn('project_instance_id', projectInstanceIds, userId, cache);
}
